import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import GlobalSearch from './components/GlobalSearch';
import { CorporateHeader, CorporateFooter } from './components/CorporateChrome';
import NotificationBell from './components/NotificationBell';
import NotificationPreferences from './pages/NotificationPreferences';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetailPage from './pages/CustomerDetailPage';
import ImageSearch from './pages/ImageSearch';
import Tickets from './pages/Tickets';
import TicketDetailPage from './pages/TicketDetailPage';
import Assets from './pages/Assets';
import AssetDetailPage from './pages/AssetDetailPage';
import Integrations from './pages/Integrations';
import KnowledgeBase from './pages/KnowledgeBase';
import Alerts from './pages/Alerts';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import UserManagement from './pages/UserManagement';
import AccountsPayable from './pages/AccountsPayable';
import AccountsReceivable from './pages/AccountsReceivable';
import Invoices from './pages/Invoices';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import ProductsAndServices from './pages/ProductsAndServices';
import ARSettings from './pages/ARSettings';
import TicketSettings from './pages/TicketSettings';
import ScriptsAndCrons from './pages/ScriptsAndCrons';
import Office365Settings from './pages/Office365Settings';
import EmailRoutingSettings from './pages/EmailRoutingSettings';
import EmailBranding from './pages/EmailBranding';
import SystemEmailTemplates from './pages/SystemEmailTemplates';
import TeamsChat from './pages/TeamsChat';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import MFASettings from './pages/MFASettings';
import MyProfile from './pages/MyProfile';
import EnomDomains from './pages/EnomDomains';
import PowerDnsPortal from './pages/PowerDnsPortal';
import Accounting from './pages/Accounting';
import ChatWidget from './components/ChatWidget';
import Office365Setup from './pages/Office365Setup';
import CustomerPortalLogin from './pages/CustomerPortalLogin';
import CustomerPortalDashboard from './pages/CustomerPortalDashboard';
import TechnicianPortalLogin from './pages/TechnicianPortalLogin';
import TechnicianPortalDashboard from './pages/TechnicianPortalDashboard';
import BitdefenderDashboard from './pages/BitdefenderDashboard';
import ScreenConnectDashboard from './pages/ScreenConnectDashboard';
import NinjaRMMDashboard from './pages/NinjaRMMDashboard';
import NinjaEndUsers from './pages/NinjaEndUsers';
import NinjaFinance from './pages/NinjaFinance';
import NinjaReports from './pages/NinjaReports';
import SyncDashboard from './pages/SyncDashboard';
import WHMCSImport from './pages/WHMCSImport';
import RepairShoprImport from './pages/RepairShoprImport';
import QuickBooksOnline from './pages/QuickBooksOnline';
import StaffSchedule from './pages/StaffSchedule';
import HumanResources from './pages/HumanResources';
import StaffDetail from './pages/StaffDetail';
import RolesPermissions from './pages/RolesPermissions';
import BlockedSenders from './pages/BlockedSenders';
import Agreements from './pages/Agreements';
import AgreementDetail from './pages/AgreementDetail';
import SlaAlerts from './pages/SlaAlerts';
import BarcodeScannerPage from './pages/BarcodeScannerPage';
import TerminalSessionViewer from './pages/TerminalSessionViewer';
import { usePermissions } from './hooks/usePermissions';
import { PortalContainerContext } from './contexts/PortalContainerContext';
import { 
  LayoutDashboard, Users, Ticket, Package, FileText, LogOut, Menu, X, 
  Zap, BookOpen, Bell, BarChart, Settings as SettingsIcon, UserCog, 
  ChevronDown, TrendingUp, DollarSign, Terminal, Mail, Shield, Monitor, Server, Database, Wrench, Target, Palette, CalendarClock, Ban, AlertTriangle, Radio, ScanLine, MessageCircle, Globe, Search, Plus, User as UserIcon
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <img 
          src="/cipherone-logo.png" 
          alt="Loading" 
          className="h-20 w-20 logo-spin mb-4"
        />
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Portal users can ONLY access tickets
  const isPortal = user?.role === 'portal_user' || user?.role === 'user' || user?.role === 'customer';
  if (isPortal) {
    const allowedPaths = ['/tickets', '/notification-preferences', '/mfa-settings', '/my-profile', '/forgot-password', '/reset-password'];
    const currentPath = window.location.pathname;
    const isAllowed = allowedPaths.some(p => currentPath.startsWith(p));
    if (!isAllowed && currentPath !== '/') {
      return <Navigate to="/tickets" replace />;
    }
    if (currentPath === '/' || currentPath === '/dashboard') {
      return <Navigate to="/tickets" replace />;
    }
  }

  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}
function AppLayout({ children }) {
  const { user, logout } = useAuth();  const { canAccessModule } = usePermissions();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [portalContainer, setPortalContainer] = React.useState(null);

  // Global search keyboard shortcut (Ctrl+K / Cmd+K)
  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close mobile menu on route change
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Module-to-permission mapping for nav items
  const navPermMap = {
    '/dashboard': 'dashboard',
    '/customers': 'customers',
    '/reports': 'reports',
    '/knowledge-base': 'knowledge_base',
    '/tickets': 'tickets',
    '/schedule': 'schedule',
    '/assets': 'assets',
    '/alerts': 'alerts',
    '/invoices': 'invoices',
    '/agreements': 'invoices',
    '/products-services': 'products_services',
    '/accounts-receivable': 'accounts_receivable',
    '/accounts-payable': 'accounts_payable',
    '/ar-settings': 'ar_settings',
    '/integrations': 'integrations',
    '/ninjarmm': 'integrations',
    '/sync-dashboard': 'integrations',
    '/bitdefender': 'integrations',
    '/screenconnect': 'integrations',
    '/quickbooks': 'integrations',
    '/office365': 'integrations',
    '/office365-setup': 'integrations',
    '/import/whmcs': 'integrations',
    '/import/repairshopr': 'integrations',
    '/human-resources': 'human_resources',
    '/users': 'user_management',
    '/admin/roles': 'roles_permissions',
    '/blocked-senders': 'ticket_settings',
    '/ticket-settings': 'ticket_settings',
    '/email-routing': 'email_routing',
    '/email-branding': 'email_branding',
    '/scripts': 'scripts',
    '/settings': 'system_settings',
  };

  const canAccessHref = (href) => {
    if (href === '/powerdns') return user?.role === 'admin'; // DNS portal is exact-admin only
    if (href === '/accounting') return user?.role === 'admin'; // ERP Accounting limited rollout: admin only
    const mod = navPermMap[href];
    if (!mod) return true; // no restriction
    return canAccessModule(mod);
  };

  const allNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    {
      name: 'Service Desk',
      icon: Ticket,
      submenu: [
        { name: 'Tickets', href: '/tickets', icon: Ticket },
        { name: 'Knowledge Base', href: '/knowledge-base', icon: BookOpen },
        { name: 'Chat', href: '/teams-chat', icon: MessageCircle },
      ]
    },
    { 
      name: 'Customers', 
      icon: Users,
      submenu: [
        { name: 'CRM Dashboard', href: '/customers', icon: Users },
        { name: 'Reports', href: '/reports', icon: BarChart },
      ]
    },
    { 
      name: 'Billing', 
      icon: FileText,
      submenu: [
        { name: 'Invoices', href: '/invoices', icon: FileText },
        { name: 'Agreements', href: '/agreements', icon: FileText },
        { name: 'Assets', href: '/assets', icon: Package },
        { name: 'Products & Services', href: '/products-services', icon: Package },
        { name: 'Accounts Receivable', href: '/accounts-receivable', icon: TrendingUp },
        { name: 'Accounts Payable', href: '/accounts-payable', icon: DollarSign },
        { name: 'Accounting (GL)', href: '/accounting', icon: BarChart },
      ]
    },
    {
      // Day-to-day operations. Split out from the former single "Manage" menu;
      // same destinations/role gates, now its own top-level dropdown.
      name: 'Operations',
      icon: Wrench,
      submenu: [
        { name: 'Schedule', href: '/schedule', icon: CalendarClock },
        { name: 'Barcode Scanner', href: '/barcode-scanner', icon: ScanLine },
        { name: 'Image Search', href: '/image-search', icon: Search },
        { name: 'SLA Alerts', href: '/sla-alerts', icon: AlertTriangle },
        { name: 'Alerts', href: '/alerts', icon: Bell },
        { name: 'Domains', href: '/domains', icon: Globe },
        { name: 'PowerDNS', href: '/powerdns', icon: Server },
        { name: 'Integrations Overview', href: '/integrations', icon: Zap },
        { name: 'NinjaRMM', href: '/ninjarmm', icon: Server },
        { name: 'Ninja End Users', href: '/integrations/ninjaone/end-users', icon: Users },
        { name: 'Ninja Finance', href: '/integrations/ninjaone/finance', icon: DollarSign },
        { name: 'Ninja Reports', href: '/integrations/ninjaone/reports', icon: BarChart },
        { name: 'Sync Dashboard', href: '/sync-dashboard', icon: Radio },
        { name: 'BitDefender', href: '/bitdefender', icon: Shield },
        { name: 'ScreenConnect', href: '/screenconnect', icon: Monitor },
        { name: 'QuickBooks Online', href: '/quickbooks', icon: DollarSign },
        { name: 'Office 365 Email', href: '/office365', icon: Mail },
        { name: 'Office 365 Setup', href: '/office365-setup', icon: SettingsIcon },
        { name: 'WHMCS Import', href: '/import/whmcs', icon: Database },
        { name: 'RepairShopr Import', href: '/import/repairshopr', icon: Wrench },
      ]
    },
    {
      // Administration & platform settings. Split out from the former single
      // "Manage" menu; same destinations/role gates, now its own dropdown.
      name: 'Admin',
      icon: Shield,
      submenu: [
        { name: 'User Management', href: '/users', icon: UserCog },
        { name: 'Human Resources', href: '/human-resources', icon: Users },
        { name: 'Roles & Permissions', href: '/admin/roles', icon: Shield },
        { name: 'Ticket Settings', href: '/ticket-settings', icon: Ticket },
        { name: 'Blocked Senders', href: '/blocked-senders', icon: Ban },
        { name: 'Email Routing & Domains', href: '/email-routing', icon: Target },
        { name: 'Email Branding', href: '/email-branding', icon: Palette },
        { name: 'Email Templates', href: '/email-templates-admin', icon: Mail },
        { name: 'AR Settings', href: '/ar-settings', icon: FileText },
        { name: 'Scripts & Cron Jobs', href: '/scripts', icon: Terminal },
        { name: 'Terminal Sessions', href: '/admin/terminal-sessions', icon: Monitor },
        { name: 'System Settings', href: '/settings', icon: SettingsIcon },
      ]
    },
  ];

  // Filter navigation based on permissions
  const isPortalUser = user?.role === 'portal_user' || user?.role === 'user' || user?.role === 'customer';
  
  const navigation = isPortalUser 
    ? [{ name: 'My Tickets', href: '/tickets', icon: Ticket }]
    : allNavigation.map(item => {
        if (item.submenu) {
          const filtered = item.submenu.filter(sub => canAccessHref(sub.href));
          if (filtered.length === 0) return null;
          return { ...item, submenu: filtered };
        }
        return canAccessHref(item.href) ? item : null;
      }).filter(Boolean);

  const isActive = (path) => location.pathname === path;
  
  const isActiveSubmenu = (submenu) => {
    return submenu.some(item => location.pathname === item.href);
  };

  // Prominent "New Ticket" nav action: only when the user can actually reach the
  // existing Tickets page + Create Ticket dialog (same route/permission already used below)
  const showNewTicketAction = isPortalUser || canAccessHref('/tickets');

  // Bottom nav items for mobile quick access
  const bottomNavItems = isPortalUser 
    ? [{ name: 'Tickets', href: '/tickets', icon: Ticket }]
    : [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Tickets', href: '/tickets', icon: Ticket },
        { name: 'Customers', href: '/customers', icon: Users },
        { name: 'Invoices', href: '/invoices', icon: FileText },
      ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#041019' }} data-testid="msp-app-shell">
      <CorporateHeader />
      {/* CipherOne Navigation - single consolidated header (brand + nav + search) */}
      <nav style={{backgroundColor: '#041019', color: '#f4f8fa', borderBottom: '1px solid #193b4b'}} className="sticky top-0 z-50" data-testid="main-nav">
        <div className="co-container">
          <div className="flex items-center justify-between" style={{minHeight: '56px'}}>
            <div className="msp-nav-links hidden xl:flex items-center space-x-1 flex-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                if (item.submenu) {
                  const active = isActiveSubmenu(item.submenu);
                  return (
                    <DropdownMenu key={item.name}>
                      <DropdownMenuTrigger asChild>
                        <button style={{whiteSpace: 'nowrap'}} className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium transition-colors rounded ${active ? 'bg-[#081a25] text-[#37c9ee]' : 'text-[#f4f8fa] hover:bg-[#081a25] hover:text-white'}`}>
                          <Icon className="w-4 h-4" />
                          <span>{item.name}</span>
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#081a25] text-[#f4f8fa] border-[#193b4b] min-w-[200px] max-h-[70vh] overflow-y-auto">
                        {item.submenu.map((subItem, idx) => {
                          const SubIcon = subItem.icon;
                          const prevGroupLabel = idx > 0 ? item.submenu[idx - 1].groupLabel : undefined;
                          const showGroupLabel = subItem.groupLabel && subItem.groupLabel !== prevGroupLabel;
                          return (
                            <React.Fragment key={subItem.href}>
                              {showGroupLabel && (
                                <div
                                  className={`px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#5f7585] ${idx > 0 ? 'border-t border-[#193b4b] mt-1' : ''}`}
                                  data-testid={`nav-group-label-${subItem.groupLabel.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  {subItem.groupLabel}
                                </div>
                              )}
                              <DropdownMenuItem asChild>
                                <Link to={subItem.href} className="flex items-center space-x-2 cursor-pointer">
                                  <SubIcon className="w-4 h-4" />
                                  <span>{subItem.name}</span>
                                </Link>
                              </DropdownMenuItem>
                            </React.Fragment>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    style={{whiteSpace: 'nowrap'}}
                    className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium transition-colors rounded ${active ? 'bg-[#081a25] text-[#37c9ee]' : 'text-[#f4f8fa] hover:bg-[#081a25] hover:text-white'}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>

            <div className="flex items-center gap-1" style={{flexShrink: 0}}>
              {showNewTicketAction && (
                <Link
                  to="/tickets?new=1"
                  data-testid="new-ticket-nav-button"
                  title="Create a new ticket"
                  className="msp-nav-link hidden sm:inline-flex items-center gap-1.5 px-3 mr-2 rounded-md text-sm font-semibold text-[#041019] bg-[#37c9ee] hover:brightness-110 transition"
                  style={{ minHeight: '44px' }}
                >
                  <Plus className="w-4 h-4" />
                  <span>New Ticket</span>
                </Link>
              )}
              <button
                onClick={() => setSearchOpen(true)}
                data-testid="global-search-button"
                title="Search (Ctrl+K)"
                className="flex items-center gap-2 px-3 py-1.5 mr-1 rounded-md text-sm text-[#b1c3ce] border border-[#193b4b] bg-[#081a25] hover:brightness-125 transition"
              >
                <Search className="w-4 h-4" />
                <span className="hidden xl:inline">Search</span>
                <span className="hidden xl:inline text-xs text-[#5f7585]">Ctrl K</span>
              </button>
              <Link
                to="/barcode-scanner"
                data-testid="header-scanner-button"
                title="Scan a barcode or QR code"
                aria-label="Open barcode and QR scanner"
                className="flex items-center justify-center w-9 h-9 mr-1 rounded-md text-[#b1c3ce] border border-[#193b4b] bg-[#081a25] hover:brightness-125 hover:text-white transition"
              >
                <ScanLine className="w-4 h-4" />
              </Link>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button style={{whiteSpace: 'nowrap'}} className="hidden lg:flex items-center space-x-2 px-4 py-2 text-sm text-[#f4f8fa] hover:bg-[#081a25] transition-colors rounded">
                    <span>{user?.full_name || user?.email}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#081a25] text-[#f4f8fa] border-[#193b4b] min-w-[200px]">
                  <DropdownMenuItem onClick={() => window.location.href = '/my-profile'} className="flex items-center space-x-2 cursor-pointer" data-testid="my-profile-link">
                    <UserIcon className="w-4 h-4" />
                    <span>My Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => window.location.href = '/notification-preferences'} className="flex items-center space-x-2 cursor-pointer" data-testid="notification-prefs-link">
                    <Bell className="w-4 h-4" />
                    <span>Notifications</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => window.location.href = '/mfa-settings'} className="flex items-center space-x-2 cursor-pointer" data-testid="mfa-settings-link">
                    <Shield className="w-4 h-4" />
                    <span>MFA Security</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="flex items-center space-x-2 cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
                className="xl:hidden p-2 text-[#f4f8fa] hover:bg-[#081a25] rounded"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Global Search Dialog (opened from header or Ctrl+K) */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Mobile Slide-Out Menu Overlay */}
      {mobileMenuOpen && (
        <div className="xl:hidden fixed inset-0 z-[60]" data-testid="mobile-menu-overlay">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-0 right-0 h-full w-[280px] bg-[#041019] shadow-2xl overflow-y-auto animate-slide-in-right">
            <div className="flex items-center justify-between p-4 border-b border-[#193b4b]">
              <span className="text-white font-semibold text-sm">{user?.full_name || user?.email}</span>
              <button onClick={() => setMobileMenuOpen(false)} className="p-1 text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            {showNewTicketAction && (
              <div className="p-4 border-b border-[#193b4b]">
                <Link
                  to="/tickets?new=1"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="new-ticket-mobile-button"
                  className="msp-nav-link w-full flex items-center justify-center gap-2 rounded-md text-sm font-semibold text-[#041019] bg-[#37c9ee] hover:brightness-110 transition"
                  style={{ minHeight: '44px' }}
                >
                  <Plus className="w-4 h-4" />
                  <span>New Ticket</span>
                </Link>
              </div>
            )}
            <div className="py-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                if (item.submenu) {
                  return (
                    <MobileMenuSection key={item.name} item={item} isActiveSubmenu={isActiveSubmenu} onNavigate={() => setMobileMenuOpen(false)} />
                  );
                }
                return (
                  <Link 
                    key={item.href} 
                    to={item.href} 
                    className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isActive(item.href) ? 'bg-[#081a25] text-[#37c9ee] border-l-2 border-[#37c9ee]' : 'text-[#b1c3ce] hover:bg-[#081a25] hover:text-white'}`} 
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              <div className="border-t border-[#193b4b] mt-2 pt-2">
                <button 
                  onClick={() => { logout(); setMobileMenuOpen(false); }} 
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-[#081a25] transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PortalContainerContext.Provider value={portalContainer}>
        <main ref={setPortalContainer} className="co-container co-main co-scope co-settings py-4 sm:py-6 pb-20 lg:pb-6">
          {children}
        </main>
      </PortalContainerContext.Provider>
      <CorporateFooter />

      {/* Mobile Bottom Navigation Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#041019] border-t border-[#193b4b] shadow-[0_-2px_10px_rgba(0,0,0,0.4)]" data-testid="bottom-nav">
        <div className="flex items-center justify-around h-14">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${active ? 'text-[#37c9ee]' : 'text-[#b1c3ce] hover:text-white'}`}
                data-testid={`bottom-nav-${item.name.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] mt-0.5 font-medium">{item.name}</span>
              </Link>
            );
          })}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${mobileMenuOpen ? 'text-[#37c9ee]' : 'text-[#b1c3ce] hover:text-white'}`}
            data-testid="bottom-nav-more"
          >
            <Menu className="w-5 h-5" />
            <span className="text-[10px] mt-0.5 font-medium">More</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Collapsible section for mobile menu
function MobileMenuSection({ item, isActiveSubmenu, onNavigate }) {
  const [open, setOpen] = React.useState(isActiveSubmenu(item.submenu));
  const Icon = item.icon;
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-colors ${isActiveSubmenu(item.submenu) ? 'text-[#37c9ee]' : 'text-[#b1c3ce] hover:text-white hover:bg-[#081a25]'}`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span>{item.name}</span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="bg-[#020b11]/60 py-1">
          {item.submenu.map((subItem, idx) => {
            const SubIcon = subItem.icon;
            const prevGroupLabel = idx > 0 ? item.submenu[idx - 1].groupLabel : undefined;
            const showGroupLabel = subItem.groupLabel && subItem.groupLabel !== prevGroupLabel;
            return (
              <React.Fragment key={subItem.href}>
                {showGroupLabel && (
                  <div
                    className={`pl-11 pr-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#5f7585] ${idx > 0 ? 'border-t border-[#193b4b] mt-1' : ''}`}
                    data-testid={`nav-group-label-mobile-${subItem.groupLabel.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {subItem.groupLabel}
                  </div>
                )}
                <Link
                  to={subItem.href}
                  className="flex items-center gap-3 pl-11 pr-4 py-2.5 text-sm text-[#b1c3ce] hover:text-white hover:bg-[#081a25] transition-colors"
                  onClick={onNavigate}
                >
                  <SubIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{subItem.name}</span>
                </Link>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/mfa-settings" element={<ProtectedRoute><AppLayout><MFASettings /></AppLayout></ProtectedRoute>} />
      <Route path="/my-profile" element={<ProtectedRoute><AppLayout><MyProfile /></AppLayout></ProtectedRoute>} />
      <Route path="/domains" element={<ProtectedRoute><AppLayout><EnomDomains /></AppLayout></ProtectedRoute>} />
      <Route path="/powerdns" element={<AdminRoute><AppLayout><PowerDnsPortal /></AppLayout></AdminRoute>} />
      <Route path="/accounting" element={<AdminRoute><AppLayout><Accounting /></AppLayout></AdminRoute>} />
      <Route path="/customer-portal/login" element={<CustomerPortalLogin />} />
      <Route path="/customer-portal/dashboard" element={<CustomerPortalDashboard />} />
      <Route path="/technician-portal/login" element={<TechnicianPortalLogin />} />
      <Route path="/technician-portal/dashboard" element={<TechnicianPortalDashboard />} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><AppLayout><Customers /></AppLayout></ProtectedRoute>} />
      <Route path="/customers/:customerId" element={<ProtectedRoute><AppLayout><CustomerDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/tickets" element={<ProtectedRoute><AppLayout><Tickets /></AppLayout></ProtectedRoute>} />
      <Route path="/tickets/:ticketId" element={<ProtectedRoute><AppLayout><TicketDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/image-search" element={<ProtectedRoute><AppLayout><ImageSearch /></AppLayout></ProtectedRoute>} />
      <Route path="/assets" element={<ProtectedRoute><AppLayout><Assets /></AppLayout></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><AppLayout><StaffSchedule /></AppLayout></ProtectedRoute>} />
      <Route path="/sla-alerts" element={<ProtectedRoute><AppLayout><SlaAlerts /></AppLayout></ProtectedRoute>} />
      <Route path="/human-resources" element={<ProtectedRoute><AppLayout><HumanResources /></AppLayout></ProtectedRoute>} />
      <Route path="/human-resources/:userId" element={<ProtectedRoute><AppLayout><StaffDetail /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/roles" element={<ProtectedRoute><AppLayout><RolesPermissions /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/terminal-sessions" element={<ProtectedRoute><AppLayout><TerminalSessionViewer /></AppLayout></ProtectedRoute>} />
      <Route path="/blocked-senders" element={<ProtectedRoute><AppLayout><BlockedSenders /></AppLayout></ProtectedRoute>} />
      <Route path="/agreements" element={<ProtectedRoute><AppLayout><Agreements /></AppLayout></ProtectedRoute>} />
      <Route path="/agreements/:id" element={<ProtectedRoute><AppLayout><AgreementDetail /></AppLayout></ProtectedRoute>} />
      <Route path="/assets/:assetId" element={<ProtectedRoute><AppLayout><AssetDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/barcode-scanner" element={<ProtectedRoute><AppLayout><BarcodeScannerPage /></AppLayout></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><AppLayout><Invoices /></AppLayout></ProtectedRoute>} />
      <Route path="/invoices/:invoiceId" element={<ProtectedRoute><AppLayout><InvoiceDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/products-services" element={<ProtectedRoute><AppLayout><ProductsAndServices /></AppLayout></ProtectedRoute>} />
      <Route path="/accounts-payable" element={<ProtectedRoute><AppLayout><AccountsPayable /></AppLayout></ProtectedRoute>} />
      <Route path="/accounts-receivable" element={<ProtectedRoute><AppLayout><AccountsReceivable /></AppLayout></ProtectedRoute>} />
      <Route path="/ar-settings" element={<ProtectedRoute><AppLayout><ARSettings /></AppLayout></ProtectedRoute>} />
      <Route path="/ticket-settings" element={<ProtectedRoute><AppLayout><TicketSettings /></AppLayout></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><AppLayout><Integrations /></AppLayout></ProtectedRoute>} />
      <Route path="/ninjarmm" element={<ProtectedRoute><AppLayout><NinjaRMMDashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/integrations/ninjaone/end-users" element={<ProtectedRoute><AppLayout><NinjaEndUsers /></AppLayout></ProtectedRoute>} />
      <Route path="/integrations/ninjaone/end-users/:bindingId" element={<ProtectedRoute><AppLayout><NinjaEndUsers /></AppLayout></ProtectedRoute>} />
      <Route path="/integrations/ninjaone/finance" element={<ProtectedRoute><AppLayout><NinjaFinance /></AppLayout></ProtectedRoute>} />
      <Route path="/integrations/ninjaone/reports" element={<ProtectedRoute><AppLayout><NinjaReports /></AppLayout></ProtectedRoute>} />
      <Route path="/sync-dashboard" element={<ProtectedRoute><AppLayout><SyncDashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/bitdefender" element={<ProtectedRoute><AppLayout><BitdefenderDashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/screenconnect" element={<ProtectedRoute><AppLayout><ScreenConnectDashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/office365" element={<ProtectedRoute><AppLayout><Office365Settings /></AppLayout></ProtectedRoute>} />
      <Route path="/office365-setup" element={<ProtectedRoute><AppLayout><Office365Setup /></AppLayout></ProtectedRoute>} />
      <Route path="/email-routing" element={<ProtectedRoute><AppLayout><EmailRoutingSettings /></AppLayout></ProtectedRoute>} />
      <Route path="/email-branding" element={<ProtectedRoute><AppLayout><EmailBranding /></AppLayout></ProtectedRoute>} />
      <Route path="/email-templates-admin" element={<ProtectedRoute><AppLayout><SystemEmailTemplates /></AppLayout></ProtectedRoute>} />
      <Route path="/teams-chat" element={<ProtectedRoute><AppLayout><TeamsChat /></AppLayout></ProtectedRoute>} />
      <Route path="/import/whmcs" element={<ProtectedRoute><AppLayout><WHMCSImport /></AppLayout></ProtectedRoute>} />
      <Route path="/import/repairshopr" element={<ProtectedRoute><AppLayout><RepairShoprImport /></AppLayout></ProtectedRoute>} />
      <Route path="/quickbooks" element={<ProtectedRoute><AppLayout><QuickBooksOnline /></AppLayout></ProtectedRoute>} />
      <Route path="/knowledge-base" element={<ProtectedRoute><AppLayout><KnowledgeBase /></AppLayout></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><AppLayout><Alerts /></AppLayout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><AppLayout><Reports /></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppLayout><Settings /></AppLayout></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><AppLayout><UserManagement /></AppLayout></ProtectedRoute>} />
      <Route path="/scripts" element={<ProtectedRoute><AppLayout><ScriptsAndCrons /></AppLayout></ProtectedRoute>} />
      <Route path="/notification-preferences" element={<ProtectedRoute><AppLayout><NotificationPreferences /></AppLayout></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <ChatWidget />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
